import React, { useState, useEffect, useRef } from 'react';
import i18n from '@nextroll/ar-i18n';

import { InputGroup } from '@nextroll/ar-style-base';
import { useContainerContext } from '@nextroll/ar-container-context';

import useAbortController from 'hooks/useAbortController';
import { RequestError } from 'hooks/useRequest';
import { ActionMetadataProperty } from 'types/rofl';
import { InputPropertyFieldBase } from 'types/propertyGeneration';
import { ActionPropertyValuesResult, LookupType } from 'types/roflWorkflowActionProperty';
import { useRoflStore } from 'store/roflStore';
import { getPropertyInputErrorMessage, getValidationError, isValidPropertyValue, ValidationError } from 'util/errors';
import { WORKFLOW_ACTION_PROPERTIES, WORKFLOW_ACTIONS } from 'util/workflow.constants';

import { InputErrorElement, InputType } from 'components/basic/inputProperty/InputErrorElement/InputErrorElement';

/**
 *  SingleLookupProps are the type of props needed to update a workflow from this component
 */
export interface SingleLookupUpdateWorkflowParams {
    value: any;
    actionMetadataProperty: ActionMetadataProperty;
}

/**
 *  SingleLookupProps represent the props for the single lookup component
 */
export interface SingleLookupProps extends InputPropertyFieldBase<SingleLookupUpdateWorkflowParams> {
    getData: (config: {
        lookupText: string;
        abortSignal?: AbortSignal;
        onError?: ((requestError: RequestError) => boolean) | undefined;}) => Promise<ActionPropertyValuesResult>;
}

/**
 * In action node > Marketo > add contact to a smart campign, this is the error message when the URL is not valid.
 */
const MarketoSmartCampaignNotFoundMsg = () => (
    <>
        <span>{i18n.gettext('No matching Smart Campaign found or the Smart Campaign is not configured to work with AdRoll Workflows.')}</span>
        <p>{i18n.gettext('Before using a Smart Campaign in a AdRoll Workflow, go to Marketo and check the following settings:')}</p>
        <ul>
            <li>{i18n.gettext('Use "Campaign is Requested" as the trigger for the campaign')}</li>
            <li>{i18n.gettext('Set the "Campaign is Requested" source to "Web Service API"')}</li>
            <li>{i18n.gettext('The Smart Campaign status should be "Active"')}</li>
        </ul>
    </>
);

/**
 *  This component represents a textfield where a user enters text, that text is sent to our api on the "blur"
 *  event, and the returning value is saved on the current workflow
 */
export const SingleLookup = (props: SingleLookupProps) => {
    const { advertisable: { eid: advertisableEid = '' } = {} } = useContainerContext();
    const [lookupSearch, setLookupSearch] = useState<string>(props.actionMetadataProperty?.lookup?.single_lookup?.text || '');
    const [displayValue, setDisplayValue] = useState<string | undefined>(
        props.actionMetadataProperty?.lookup?.single_lookup?.selected.display_value
    );
    const [error, setError] = useState<React.ReactNode>();
    const [isLookupSearchUpdating, setIsLookupSearchUpdating] = useState<boolean>();
    const shouldShowRequiredError = useRoflStore(state => state.shouldShowRequiredError);
    const lookupFieldDisplayName = props.inputProperty.lookup?.single_lookup?.display_name || '';
    const getSignal = useAbortController();
    const onLookupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsLookupSearchUpdating(true);
        setLookupSearch(e.target.value);
    };

    const { identifier, setIsValid, propertyError } = props;
    // since we only want this to run once (on component mount), with no dependencies,
    // we use a useRef.
    const setInitialPropertyError = useRef(() => {
        if (propertyError?.error) {
            setError(getValidationError(propertyError.error, lookupFieldDisplayName));
            setIsValid((isValid: Record<string, boolean>) => {
                return { ...isValid, [identifier]: false };
            });
        }
    });
    useEffect(() => {
        setInitialPropertyError.current();
    }, []);

    useEffect(() => {
        if (!lookupSearch && shouldShowRequiredError) {
            setError(getValidationError(ValidationError.GENERIC_REQUIRED, lookupFieldDisplayName));
        }
        setIsValid((isValid: Record<string, boolean>) => {
            return { ...isValid, [identifier]: !!lookupSearch && !isLookupSearchUpdating && error === undefined };
        });
    }, [error,
        identifier,
        isLookupSearchUpdating,
        lookupFieldDisplayName,
        lookupSearch,
        props.inputProperty.required,
        shouldShowRequiredError,
        setIsValid]
    );

    /**
     * Validate the lookup content. This is usually triggered by the onBlur event of the input.
     */
    const onValidateLookup = async () => {
        // return immediately if we havent updated anything
        const showRequiredError = props.inputProperty.required && !lookupSearch;
        const valNotUpdated = (lookupSearch && (!isLookupSearchUpdating || !advertisableEid));
        if (showRequiredError || valNotUpdated) {
            return;
        }

        // quick validation before we fire off an api request
        if (!isValidPropertyValue(lookupSearch, props.inputProperty.lookup?.single_lookup?.validation)) {
            setError(getValidationError(ValidationError.INVALID_INPUT, lookupFieldDisplayName));
            return;
        }

        const abortSignal = getSignal();
        const handleError = (requestError: RequestError) => {
            if (abortSignal.aborted) {
                return true;
            }
            if (props.workflowActionEid === WORKFLOW_ACTIONS.MARKETO_ADD_CONTACT_TO_CAMPAIGN
                && props.inputProperty.name === WORKFLOW_ACTION_PROPERTIES[props.workflowActionEid].CAMPAIGN_ID) {
                setError(<MarketoSmartCampaignNotFoundMsg />);
            } else {
                setError(getPropertyInputErrorMessage(requestError, lookupFieldDisplayName));
            }
            setDisplayValue(undefined);
            return true;
        };
        const result = await props.getData({
            lookupText: lookupSearch,
            abortSignal,
            onError: handleError,
        });

        if (abortSignal.aborted || !result) {
            return;
        }

        // process the valid response value
        setDisplayValue(result.display_value);
        props.updateWorkflow({
            value: result.value,
            actionMetadataProperty: {
                lookup: {
                    type: LookupType.SINGLE,
                    single_lookup: {
                        text: lookupSearch,
                        selected: {
                            display_value: result.display_value,
                        }
                    }
                }
            }
        });
        setIsLookupSearchUpdating(false);
        setError(undefined);
    };

    return (
        <div className='single-lookup'>
            <p className='single-lookup-label'>{props.inputProperty.lookup?.single_lookup?.display_name}</p>
            <InputGroup
                i18n={i18n.noop}
                classNames='single-lookup-input'
                validationState={error ? InputGroup.VALIDATION_ERROR : undefined}
                inputProps={{
                    placeholder: i18n.gettext('Enter Text'),
                    onChange: onLookupChange,
                    onBlur: onValidateLookup,
                    value: lookupSearch
                }}
            />
            {error && (
                <>
                    <hr />
                    <InputErrorElement
                        value={displayValue}
                        inputType={InputType.TEXT}
                        error={error}
                        propertyError={propertyError}
                        setError={setError}
                        setIsValid={setIsValid}
                        fieldIsValid={!!lookupSearch && !isLookupSearchUpdating}
                        errorClassName='single-lookup-error-msg'
                        identifier={identifier}
                        entityTitle={lookupFieldDisplayName}
                        serviceTitle={props.serviceTitle}
                    />
                    <hr />
                </>
            )}
            { !error && displayValue && (
                <>
                    <span className='single-lookup-label'>{props.inputProperty.display_name}</span>
                    <hr />
                    <span className={isLookupSearchUpdating ? 'single-lookup-label' : ''}>
                        {displayValue}
                    </span>
                    <hr />
                </>
            )}
        </div>
    );
};
